import {inject, Injectable} from '@angular/core';
import {lastValueFrom} from 'rxjs';
import {injectMutation, QueryClient} from '@tanstack/angular-query-experimental';
import {
  CreateLicenceServerRequest,
  LicenceServerService,
  UpdateLicenceServerRequest,
} from '../../../../../../api';
import {QueryKey} from '../../../../../constants/query-key';

@Injectable()
export class LicenceServerCommandService {
  private readonly licenceServerService = inject(LicenceServerService);
  private readonly queryClient = inject(QueryClient);

  createMutation = injectMutation(() => ({
    mutationFn: (request: CreateLicenceServerRequest) =>
      lastValueFrom(this.licenceServerService.createLicenceServer(request)),
    onSuccess: () => this.invalidateLicenceServerQueries(),
  }));

  updateMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdateLicenceServerRequest }) =>
      lastValueFrom(this.licenceServerService.updateLicenceServer(request.id, request.body)),
    onSuccess: () => this.invalidateLicenceServerQueries(),
  }));

  deleteMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.licenceServerService.deleteLicenceServer(id)),
    onSuccess: () => this.invalidateLicenceServerQueries(),
  }));

  // Generates (or rotates) a server's credentials. The summary list is unaffected, so no invalidation.
  generateCredentialMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.licenceServerService.generateLicenceServerCredential(id)),
  }));

  // Attaching/detaching products to a server flips their licenceServerId/Name,
  // which drives the "linked to a server" chip in the customer-products list, so refresh it too.
  private invalidateLicenceServerQueries() {
    this.queryClient.invalidateQueries({queryKey: [QueryKey.LICENCE_SERVERS]});
    this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_CUSTOMERS]});
  }
}
